import React from 'react';
import { useIntl } from 'gatsby-plugin-react-intl';
import { MdMenu } from 'react-icons/md';
import { graphql, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';

import { Toolbar, Box, Popper, Fade, IconButton, ClickAwayListener } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { AppBar, Button, Link, ReactSVG, Typography } from '../theme';
import { colors, typo } from '../../gatsby-theme-material-ui-top-layout/theme';
import Sidebar, { LanguageLink } from './Sidebar';
import LogoSmall from '../../assets/images/logo/logo_komm-kickern_small.svg';
import LogoEn from '../../assets/images/logo/logo_komm-kickern-en.png';
import LogoDe from '../../assets/images/logo/logo_komm-kickern-de.png';

function Navbar({ location }) {
  const data = useStaticQuery(graphql`
    query {
      allStrapiHeaderMenu {
        nodes {
          menuLinks {
            DisplayTitle
            url
            id
          }
        }
      }
    }
  `);

  const homeLink = data.allStrapiHeaderMenu.nodes[0].menuLinks.slice(0, 1);
  const navLinks = data.allStrapiHeaderMenu.nodes[0].menuLinks.slice(
    1,
    data.allStrapiHeaderMenu.nodes[0].menuLinks.length
  );

  const intl = useIntl();

  // Sidebar Toggle
  const [isOpen, setIsOpen] = React.useState(false);

  const toggleSidebar = () => {
    setIsOpen((prev) => !prev);
  };
  const hideSidebar = () => {
    setIsOpen(false);
  };

  // Logo Popup for asset download
  const [logoEl, setLogoEl] = React.useState(null);
  const lpopen = Boolean(logoEl);
  const lpid = lpopen ? 'transitions-logo-popper' : undefined;

  const handleLogoClick = (event) => {
    event.preventDefault();
    setLogoEl(logoEl ? null : event.currentTarget);
  };

  const handleLogoClose = () => {
    setLogoEl(null);
  };

  return (
    <>
      <AppBar position="sticky">
        <ClickAwayListener onClickAway={hideSidebar}>
          <div>
            <Toolbar
              sx={(theme) => ({
                height: '78px',
                justifyContent: 'space-between',
                padding: '20px 24px',
                [theme.breakpoints.up('md')]: {
                  height: '78px',
                  padding: '20px 108px',
                },
              })}
            >
              <Box sx={{ flex: 1 }}>
                {homeLink.map((link) => (
                  <Link
                    underline="none"
                    color="inherit"
                    to={link.url}
                    key={link.id}
                    sx={(theme) => ({
                      display: 'block',
                      fontSize: theme.typography.pxToRem(24),
                      height: '35px',
                      lineHeight: '35px',
                    })}
                    onContextMenu={handleLogoClick}
                  >
                    <Box
                      component="img"
                      sx={{ height: '35px', width: 'auto' }}
                      src={process.env.CURRENT_LANGUAGE === 'de' ? LogoDe : LogoEn}
                      alt={intl.formatMessage({ id: 'slogan' })}
                    />
                  </Link>
                ))}
              </Box>
              <Popper
                id={lpid}
                open={lpopen}
                anchorEl={logoEl}
                transition
                sx={(theme) => ({
                  // margin: '30px 10px',
                  paddingTop: '30px',
                  textAlign: 'center',
                  color: theme.palette.primary.main,
                  maxWidth: 'calc(100vw - 40px)',
                })}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={350}>
                    <Box sx={{ borderRadius: '25px', backgroundColor: '#fff' }}>
                      <Box component="div" m={1} sx={{ padding: '25px' }}>
                        <Typography
                          variant="h2"
                          sx={(theme) => ({
                            display: 'block',
                            // Roboto-Blackitalic
                            fontSize: theme.typography.pxToRem(24),
                            fontWeight: typo.weights.bolder,
                            color: colors.kkimargenta,
                            textTransform: 'uppercase',
                            fontStyle: 'italic',
                            letterSpacing: -0.09,
                            [theme.breakpoints.up('lg')]: {
                              fontSize: theme.typography.pxToRem(26),
                            },
                          })}
                        >
                          {intl.formatMessage({ id: `assetPopup.title` })}
                        </Typography>
                        <ReactSVG
                          src={LogoSmall}
                          sx={{
                            backgroundColor: '#ddd',
                            padding: '20px 0',
                            margin: '20px 0',
                            '& svg': { height: '123px', maxWidth: 'calc(100vw - 120px)' },
                          }}
                        />
                        <Box m={1}>
                          <Button
                            variant="contained"
                            color="primary"
                            to="/press#logo-download"
                            onClick={handleLogoClose}
                          >
                            {intl.formatMessage({ id: `assetPopup.download` })}
                          </Button>
                        </Box>
                        <Box m={1}>
                          <Button variant="contained" color="secondary" to="/press" onClick={handleLogoClose}>
                            {intl.formatMessage({ id: `assetPopup.more` })}
                          </Button>
                        </Box>
                        <IconButton
                          onClick={handleLogoClose}
                          aria-label="close"
                          sx={{ position: 'absolute', right: '5px', top: '45px' }}
                          size="large"
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Fade>
                )}
              </Popper>
              <Box
                sx={(theme) => ({
                  display: 'none',
                  flex: 1,
                  justifyContent: 'flex-end',
                  [theme.breakpoints.up('sm')]: {
                    display: 'flex',
                  },
                })}
              >
                {navLinks.map((link) => (
                  <Link
                    variant="inherit"
                    underline="none"
                    color="inherit"
                    to={link.url}
                    key={link.id}
                    sx={(theme) => ({
                      color: theme.palette.common.white,
                      float: 'left',
                      fontSize: theme.typography.pxToRem(16),
                      fontWeight: theme.typography.fontWeightMedium,
                      lineHeight: '46px',
                      marginRight: theme.spacing(4),
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    })}
                  >
                    {link.DisplayTitle}
                  </Link>
                ))}

                <LanguageLink location={location} />

                <Button
                  variant="contained"
                  color="secondary"
                  to="#App-Download"
                  sx={(theme) => ({
                    [theme.breakpoints.down('md')]: { display: 'none' },
                  })}
                >
                  Download
                </Button>
              </Box>
              <Box
                component="button"
                type="button"
                sx={(theme) => ({
                  background: 'none',
                  border: 'none',
                  color: theme.palette.common.white,
                  cursor: 'pointer',
                  fontSize: '30px',
                  outline: 'none',
                  margin: '0',
                  padding: '0',
                  transition: 'transform 400ms',
                  height: '30px',
                  width: '30px',
                  [theme.breakpoints.up('sm')]: {
                    display: 'none',
                  },
                  transform: isOpen ? 'rotate(90deg)' : undefined,
                })}
                onClick={toggleSidebar}
              >
                <MdMenu />
              </Box>
            </Toolbar>
            <Sidebar isOpen={isOpen} toggleSidebar={toggleSidebar} location={location} />
          </div>
        </ClickAwayListener>
      </AppBar>
      <Box sx={{ height: '40px' }} />
    </>
  );
}

Navbar.propTypes = {
  location: PropTypes.string.isRequired,
};

export default Navbar;
